/**
 * Voorloopbreedtes van Raleway 400 in em, gemeten met canvas `measureText` op
 * 1000 px. Letters met een accent vallen terug op hun basisletter (NFD), alle
 * andere onbekende tekens op {@link FALLBACK_CHAR_WIDTH}.
 *
 * Vroeger telde het afbreken tekens met een gemiddelde breedte. Dat liet
 * smalle woorden als "Irresponsibility" (veel i, r, l, t) onnodig breken terwijl
 * er ruimte genoeg was, en bracht brede woorden juist tegen de rand. De echte
 * breedtes zitten daarom in een tabel in plaats van in een DOM-meting: zo blijft
 * het afbreken puur, zonder browser testbaar en deterministisch tussen scherm en
 * print, en hangt het niet af van of het lettertype al geladen is. Kerning laat
 * de tabel weg. Die maakt tekst enkel smaller (nagemeten over alle 768
 * lijn-keynotes), dus de som is een veilige bovengrens.
 */
const RALEWAY_CHAR_WIDTHS: Readonly<Record<string, number>> = {
  ' ': 0.255, '!': 0.252, '"': 0.292, '#': 0.68, '$': 0.619, '%': 0.714, '&': 0.667, '\'': 0.197,
  '(': 0.269, ')': 0.268, '*': 0.321, '+': 0.415, ',': 0.225, '-': 0.422, '.': 0.189, '/': 0.604,
  '0': 0.612, '1': 0.428, '2': 0.521, '3': 0.534, '4': 0.546, '5': 0.545, '6': 0.603, '7': 0.535,
  '8': 0.593, '9': 0.586, ':': 0.188, ';': 0.205, '<': 0.508, '=': 0.438, '>': 0.508, '?': 0.476,
  '@': 0.837, 'A': 0.676, 'B': 0.666, 'C': 0.686, 'D': 0.715, 'E': 0.609, 'F': 0.589, 'G': 0.716,
  'H': 0.738, 'I': 0.248, 'J': 0.481, 'K': 0.643, 'L': 0.583, 'M': 0.877, 'N': 0.772, 'O': 0.751,
  'P': 0.622, 'Q': 0.749, 'R': 0.653, 'S': 0.607, 'T': 0.61, 'U': 0.755, 'V': 0.679, 'W': 1.046,
  'X': 0.632, 'Y': 0.651, 'Z': 0.629, '[': 0.258, '\\': 0.568, ']': 0.258, '^': 0.539, '_': 0.511,
  '`': 0.222, 'a': 0.545, 'b': 0.617, 'c': 0.549, 'd': 0.622, 'e': 0.587, 'f': 0.33, 'g': 0.618,
  'h': 0.583, 'i': 0.218, 'j': 0.225, 'k': 0.53, 'l': 0.272, 'm': 0.919, 'n': 0.583, 'o': 0.595,
  'p': 0.617, 'q': 0.617, 'r': 0.351, 's': 0.487, 't': 0.335, 'u': 0.594, 'v': 0.533, 'w': 0.813,
  'x': 0.504, 'y': 0.542, 'z': 0.492, '{': 0.259, '|': 0.231, '}': 0.259, '~': 0.511,
  '‘': 0.196, '’': 0.19, '…': 0.457,
};

/** Breedte voor een teken buiten de tabel: ruimer dan elke kleine letter behalve m en w. */
const FALLBACK_CHAR_WIDTH = 0.62;

/** Hoogte van hoofdletters en stokken (H, d, l) boven de basislijn, in em. */
export const RALEWAY_CAP_HEIGHT = 0.74;

/** Diepte van staarten (g, j, p, y) onder de basislijn, in em. */
export const RALEWAY_DESCENT = 0.24;

function charWidth(char: string): number {
  return RALEWAY_CHAR_WIDTHS[char]
    ?? RALEWAY_CHAR_WIDTHS[char.normalize('NFD').charAt(0)]
    ?? FALLBACK_CHAR_WIDTH;
}

/** Breedte van een tekst in Raleway 400, in dezelfde eenheid als `fontSize`. */
export function textWidth(text: string, fontSize: number): number {
  let width = 0;
  for (const char of text) width += charWidth(char);
  return width * fontSize;
}

/** Breedte van de koorde op afstand `offset` van het middelpunt van een cirkel. */
export function chordWidth(radius: number, offset: number): number {
  return Math.abs(offset) >= radius ? 0 : 2 * Math.sqrt(radius * radius - offset * offset);
}

/**
 * Langste begin van `word` dat binnen `maxWidth` past, minstens één teken.
 * Bewust zonder koppelteken: dat zou op een willekeurige plek staan en
 * suggereren dat er een lettergreepgrens is waar die er niet is.
 */
function fittingPrefix(word: string, fontSize: number, maxWidth: number): string {
  let length = 1;
  while (length < word.length && textWidth(word.slice(0, length + 1), fontSize) <= maxWidth) length++;
  return word.slice(0, length);
}

/**
 * Breekt een label vooraf af in regels die binnen `maxWidth` passen. Een array
 * geeft per regel een eigen breedte (index 0 = eerste regel; de laatste waarde
 * geldt voor alle volgende regels), voor tekst in een cirkel waar de ruimte
 * per hoogte verschilt.
 *
 * Breekt bij voorkeur op woordgrenzen. Een woord dat op zichzelf breder is dan
 * de regel wordt hard afgebroken in plaats van te laten uitsteken — anders
 * loopt een samenstelling als "Aanpassingsvermogen" dwars uit zijn cirkel.
 * Bij meer dan `maxLines` regels wordt de laatste regel afgekapt met een
 * ellips die zelf ook binnen de breedte blijft; de volledige tekst blijft in
 * de tooltip staan.
 */
export function wrapLabel(
  text: string,
  fontSize: number,
  maxWidth: number | readonly number[],
  maxLines: number,
): string[] {
  const widthOf = (row: number): number => typeof maxWidth === 'number'
    ? maxWidth
    : maxWidth[Math.min(row, maxWidth.length - 1)];
  const words = text.split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    let rest = word;
    while (rest.length > 0) {
      const limit = widthOf(lines.length);
      const candidate = current.length > 0 ? `${current} ${rest}` : rest;
      if (textWidth(candidate, fontSize) <= limit) {
        current = candidate;
        rest = '';
        continue;
      }
      if (current.length > 0) {
        lines.push(current);
        current = '';
        continue;
      }
      const head = fittingPrefix(rest, fontSize, limit);
      lines.push(head);
      rest = rest.slice(head.length);
    }
  }
  if (current.length > 0) lines.push(current);

  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const limit = widthOf(maxLines - 1);
  let last = kept[maxLines - 1].replace(/[\s.,;:]+$/, '');
  while (last.length > 1 && textWidth(`${last}…`, fontSize) > limit) {
    last = last.slice(0, -1).replace(/[\s.,;:]+$/, '');
  }
  kept[maxLines - 1] = `${last}…`;
  return kept;
}

/**
 * Breekt een label af in een cirkel, met zo weinig mogelijk regels.
 * `rowWidths(n)` geeft de beschikbare breedte per regel wanneer het label n
 * regels telt: het tekstblok staat gecentreerd, dus elke extra regel schuift de
 * andere naar de rand, waar de cirkel smaller is.
 *
 * Regel i krijgt bij n regels de smalste breedte die hij in elke opbouw met
 * minder regels ook heeft. Zonder dat zou een poging met twee regels een tekst
 * op één regel kunnen leggen die enkel op de bredere bovenste regel van die
 * opbouw past; getekend als één regel zakt hij dan naar het smallere midden en
 * steekt hij uit.
 */
export function wrapLabelInCircle(
  text: string,
  fontSize: number,
  maxLines: number,
  rowWidths: (lineCount: number) => readonly number[],
): string[] {
  const safeWidths = (lineCount: number): number[] => rowWidths(lineCount).map((width, row) => {
    let narrowest = width;
    for (let fewer = row + 1; fewer < lineCount; fewer++) {
      narrowest = Math.min(narrowest, rowWidths(fewer)[row]);
    }
    return narrowest;
  });

  for (let lineCount = 1; lineCount < maxLines; lineCount++) {
    const lines = wrapLabel(text, fontSize, safeWidths(lineCount), maxLines);
    if (lines.length <= lineCount) return lines;
  }
  return wrapLabel(text, fontSize, safeWidths(maxLines), maxLines);
}
